import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import Log from 'utils/log';

/**
 * The state will be shaped like this:
 * 1) call registerKeyValueClient(clientId) => registeredClients = {clientId: {}}
 * 2) call setValue(clientId, key, value)
 * 3) useSelect(() => getValue(clientId, key)
 * @type {{registered: {}}}
 */
const initialState = {
    registeredClients: {},
};

/**
 * This is a generic store that allows storing anonymous pairs of key=>value from anywhere.
 */
class KeyValueSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!KeyValueSliceHandler.instance) {
            KeyValueSliceHandler.instance = new KeyValueSliceHandler();
        }

        return KeyValueSliceHandler.instance;
    }

    constructor() {
        super('keyValueSlice');

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                /**
                 * Create a sliding layer
                 * @param {object} state
                 * @param {{payload: string: initialData?: object|null}} action
                 */
                addRegisteredClient(state, action) {
                    const clientId = action.payload.clientId;
                    const initialData = action.payload.initialData || {};

                    if (typeof state.registeredClients[clientId] !== 'undefined') {
                        // client already registered
                        return;
                    }

                    state.registeredClients[clientId] = initialData;

                    Log.debug('KEY VALUE HANDLER', `Registering client with id ${clientId}`);
                },
                /**
                 * Unregister a client.
                 * @param {object} state
                 * @param {{payload: string}} action The id of the removed client.
                 */
                removeRegisteredClient(state, action) {
                    const clientId = action.payload;

                    if (typeof state.registeredClients[clientId] === 'undefined') {
                        // client already removed
                        return;
                    }

                    const newClients = { ...state.registeredClients };
                    delete newClients[clientId];

                    state.registeredClients = { ...newClients };

                    Log.debug('KEY VALUE HANDLER', `Unregistered client with id ${clientId}`);
                },
                /**
                 *
                 * @param {object} state
                 * @param {{payload: {clientId: string, key: string, value: any}}} action
                 */
                updateKeyValue(state, action) {
                    state.registeredClients[action.payload.clientId][action.payload.key] = action.payload.value;
                },
            },
        });
    }

    /**
     * Register a client which will be able to store key values.
     * @param {string} clientId
     * @param {object|null} initialData An object with key values.
     */
    registerKeyValueClient = (clientId, initialData = null) => {
        this.dispatch('addRegisteredClient', { clientId, initialData });
    };

    /**
     * Unregister a client.
     * @param {string} clientId
     */
    unregisterKeyValueClient = (clientId) => {
        this.dispatch('removeRegisteredClient', clientId);
    };

    /**
     * Set a value associated with a key.
     * @param {string} clientId
     * @param {string} key
     * @param {any} value
     */
    setKeyValue = (clientId, key, value) => {
        this.dispatch('updateKeyValue', {
            clientId,
            key,
            value,
        });
    };

    /**
     * Get the value associated with a key.
     * @param {string} clientId
     * @param {string} key
     * @param {any} defaultValue
     */
    getKeyValue = (clientId, key, defaultValue = null) => {
        const state = this.getOwnState();

        if (typeof state.registeredClients[clientId] === 'undefined') {
            return defaultValue;
        }

        if (typeof state.registeredClients[clientId][key] === 'undefined') {
            return defaultValue;
        }

        return state.registeredClients[clientId][key];
    };
}

// the handler
export const keyValueSliceHandler = KeyValueSliceHandler.getInstance();
export const { registerKeyValueClient, unregisterKeyValueClient, setKeyValue, getKeyValue } = keyValueSliceHandler;
